/**
 * React hook for automatic route/view tracking with Grafana Faro
 *
 * This hook tracks route changes and reports them to Faro for
 * page view analytics and performance monitoring.
 *
 * @example
 * // In your App or Router component
 * function AppContent() {
 *   useRouteTracking();
 *   return <RouterProvider router={router} />;
 * }
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { setView, trackEvent, isTelemetryEnabled } from './faro';

/**
 * Options for route tracking
 */
interface RouteTrackingOptions {
  /**
   * Whether to track the initial page load
   * @default true
   */
  trackInitialLoad?: boolean;

  /**
   * Custom function to derive view name from pathname
   * @default Uses pathname as view name
   */
  getViewName?: (pathname: string) => string;

  /**
   * Paths to exclude from tracking (e.g., callback routes)
   */
  excludePaths?: string[];
}

/**
 * Default view name derivation - converts pathname to readable name
 *
 * /employees/123/edit -> "Employees Edit"
 * /admin/users -> "Admin Users"
 * / -> "Home"
 */
function defaultGetViewName(pathname: string): string {
  if (pathname === '/') return 'Home';

  return pathname
    .split('/')
    .filter((segment) => segment && !/^\d+$/.test(segment) && !/^[a-f0-9-]{36}$/i.test(segment))
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '))
    .join(' ');
}

/**
 * Hook to automatically track route changes with Faro
 *
 * Place this hook in a component that's inside the Router context
 * but wraps your main application content.
 *
 * @example
 * function AppContent() {
 *   useRouteTracking();
 *   return <Outlet />;
 * }
 *
 * @example
 * // With custom options
 * useRouteTracking({
 *   excludePaths: ['/login-callback', '/logout-callback'],
 *   getViewName: (path) => `maja:${path}`,
 * });
 */
export function useRouteTracking(options: RouteTrackingOptions = {}): void {
  const {
    trackInitialLoad = true,
    getViewName = defaultGetViewName,
    excludePaths = ['/login-callback', '/logout-callback', '/silent-renew'],
  } = options;

  const location = useLocation();
  const isFirstRender = useRef(true);
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    // Skip if telemetry is not enabled
    if (!isTelemetryEnabled()) return;

    const { pathname, search, hash } = location;

    // Skip excluded paths
    if (excludePaths.some((excluded) => pathname.startsWith(excluded))) {
      return;
    }

    // Skip if pathname hasn't changed (only query/hash changed)
    if (pathname === previousPathname.current) {
      return;
    }

    // Skip initial load if not tracking it
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!trackInitialLoad) {
        previousPathname.current = pathname;
        return;
      }
    }

    const viewName = getViewName(pathname);

    // Set the current view in Faro
    setView(viewName, {
      pathname,
      ...(search && { search }),
      ...(hash && { hash }),
    });

    // Also track as a navigation event for analytics
    if (previousPathname.current !== null) {
      trackEvent('navigation', {
        from: previousPathname.current,
        to: pathname,
        viewName,
      });
    }

    previousPathname.current = pathname;
  }, [location, trackInitialLoad, getViewName, excludePaths]);
}

/**
 * Hook to track a specific view/page explicitly
 *
 * Use this when you need to track virtual views that don't correspond
 * to URL changes (e.g., tabs, modals, wizards).
 *
 * @example
 * function CheckoutWizard() {
 *   const [step, setStep] = useState(1);
 *   useViewTracking(`Checkout Step ${step}`);
 *   // ...
 * }
 */
export function useViewTracking(viewName: string, attributes?: Record<string, string>): void {
  const previousView = useRef<string | null>(null);

  useEffect(() => {
    if (!isTelemetryEnabled()) return;
    if (viewName === previousView.current) return;

    setView(viewName, attributes);
    previousView.current = viewName;
  }, [viewName, attributes]);
}
